import { PlaylistItem } from '@/prisma/utils/playlist';
import {
	Box, ChakraComponent, Code, SimpleGrid, Text
} from '@chakra-ui/react';
import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import MediaPreview from '../media/preview';
import UserCard from '../user/card';

type PlaylistItemsProps = {
	items: PlaylistItem[]
	controls?: ({ playlistItem }: { playlistItem: PlaylistItem }) => JSX.Element | undefined;
	onItemDrop?: (source: PlaylistItem, target: PlaylistItem) => void;
}

type DraggablePlaylistItem = ChakraComponent<'div', {
	playlistItem: PlaylistItem;
	onDrop: (source: PlaylistItem, target: PlaylistItem) => void;
}>

const DraggablePlaylistItem: DraggablePlaylistItem = ({
	children, playlistItem, onDrop, ...rest 
}) => {
	const ref = useRef(null);
	const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
		type: 'PlaylistItem',
		item: playlistItem,
		// The collect function utilizes a "monitor" instance (see the Overview for what this is)
		// to pull important pieces of state from the DnD system.
		collect: (monitor) => ({
			isDragging: monitor.isDragging()
		})
	}));

	const [, drop] = useDrop(() => ({
		accept: ['PlaylistItem'],
		drop: (draggedItem: PlaylistItem, monitor) => {
			if (!ref.current) {
				return;
			}

			if (draggedItem.id === playlistItem.id) {
				return;
			}

			console.log('dropped', draggedItem.id, 'on', playlistItem.id);

			onDrop(draggedItem, playlistItem);
		},
		hover: (draggedItem: PlaylistItem, monitor) => {
			if (!ref.current) {
				return;
			}
			
			if (draggedItem.id === playlistItem.id) {
				return;
			}

			// console.log('hover', draggedItem.sourceTitle, 'over', media.sourceTitle);
			// const hoverBoundingRect = ref.current?.getBoundingClientRect();
			// console.log(hoverBoundingRect);
		},
	}));

	drag(drop(ref));

	return <Box
		ref={ref}
		bgColor={isDragging ? 'yellow.50' : 'green.50'}
		{...rest}
	>
		{children}
	</Box>;
};

const PlaylistItems: React.FC<PlaylistItemsProps> = ({
	items, controls, onItemDrop, 
}) => {
	const list = items.map(playlistItem => {
		return <DraggablePlaylistItem
			key={playlistItem.id}
			playlistItem={playlistItem}
			onDrop={onItemDrop}
			as="li"
			padding="4"
			borderRadius="lg"
		>
			<Box>
				<Text>{playlistItem.id}</Text>
				<MediaPreview
					media={playlistItem.media}
				/>
				<Text>Geplaatst door</Text>
				<UserCard user={playlistItem.media.owner} />
				<Code>{playlistItem.media.sourceUuid}</Code>
				{controls({ playlistItem: playlistItem })}
			</Box>
		</DraggablePlaylistItem>;
	});

	return (
		<SimpleGrid
			as="ul"
			listStyleType="none"
			spacing="4"
			columns={[1, null, 2, 3, 4]}
		>
			{list}
		</SimpleGrid>
	);
};

PlaylistItems.defaultProps = {
	controls: () => null,
	onItemDrop: (source, target) => undefined,
};

export default React.memo(PlaylistItems);
